import React from "react";
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  withStyles,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ContentWrap from "../HomePage/Components/ContentWrap";

const useStyles = makeStyles((theme) => ({
  container: {
    gap: "20px",
  },
  innerContent: {
    width: "100%",
    height: "fit-content",
  },
  branchTitle: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "20px",
    padding: "12px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      textAlign: "center",
      padding: "8px",
    },
  },
  subjectCard: {
    textAlign: "center",
    padding: "12px",
    borderRadius: "8px",
    minHeight: "100px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      padding: "8px",
      minHeight: "80px",
    },
  },
  subjectIcon: {
    fontSize: "22px",
    marginBottom: "6px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
    },
  },
  subjectName: {
    fontSize: "14px",
    color: "#666",
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
    },
  },
  rankValue: {
    fontSize: "24px",
    fontWeight: "bold",
    lineHeight: 1,
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
    },
  },
  circleWrapper: {
    position: "relative",
    display: "inline-flex",
    margin: "12px auto",
    [theme.breakpoints.down("sm")]: {
      margin: "8px auto",
    },
  },
  circleContent: {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  progressValue: {
    fontSize: "18px",
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
    },
  },
  progressSubtext: {
    fontSize: "10px",
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      fontSize: "9px",
    },
  },
  progressRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "4px",
    width: "100%",
  },
  progressBar: {
    height: "6px",
    borderRadius: "3px",
    flex: 1,
    marginLeft: "8px",
    backgroundColor: "#e0e0e0",
  },
  progressFill: {
    height: "100%",
    borderRadius: "3px",
    transition: "width 0.3s ease",
  },
  subjectHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  sectionTitle: {
    textAlign: "center",
    position: "relative",
    color: "#333",
    textTransform: "capitalize",
    "&::after": {
      content: '""',
      position: "absolute",
      left: "50%",
      bottom: 0,
      transform: "translateX(-50%)",
      width: "100%",
      height: "2px",
      backgroundColor: "#ccc",
    },
  },
}));

const CustomCircularProgress = withStyles({
  root: {
    transform: "rotate(-90deg) !important",
  },
})(CircularProgress);

const SubjectRankCard = ({ icon, name, rank, color }) => {
  const classes = useStyles();
  const subject = name === "Overall" ? "Branch Rank" : name;
  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap="0px"
        flexDirection="row"
      >
        <Typography className={classes.subjectIcon} style={{ color }}>
          {icon}
        </Typography>
        <Typography className={classes.subjectName} style={{ color }}>
          {subject}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="center">
        <Typography className={classes.rankValue} style={{ color }}>
          {rank}
        </Typography>
      </Box>
      </Box>
  );
};

const CircularProgressCard = ({
  label,
  score,
  maxScore,
  percentage,
  color,
  avgScore,
  topScore,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const progressSize = isMobile ? 70 : 120;

  const avgPercentage = Math.round((avgScore / maxScore) * 100);
  const topPercentage = Math.round((topScore / maxScore) * 100);

  const getSubjectIcon = (subject) => {
    const subjectLower = subject.toLowerCase();
    if (subjectLower.includes("physics") || subjectLower.includes("phy"))
      return "⚡";
    if (subjectLower.includes("chemistry") || subjectLower.includes("chem"))
      return "🧪";
    if (subjectLower.includes("math")) return "📐";
    if (subjectLower.includes("biology") || subjectLower.includes("bio"))
      return "🧬";
    if (subjectLower.includes("english") || subjectLower.includes("eng"))
      return "📚";
    if (subjectLower.includes("computer") || subjectLower.includes("cs"))
      return "💻";
    if (subjectLower.includes("total")) return "📊";
    return "🏆";
  };

  return (
    <Box textAlign="center">
      <Box className={classes.subjectHeader}>
        <div className={classes.subjectIcon} style={{ color }}>
          {getSubjectIcon(label)}
        </div>
        <Typography variant="subtitle2" style={{ color, fontWeight: "bold" }}>
          {label}
        </Typography>
      </Box>

      <Typography
        variant="caption"
        color="textSecondary"
        style={{ fontWeight: "bold" }}
      >
        Avg: <span style={{ color: "#8B5CF6" }}>{avgScore}</span> | Top:{" "}
        <span style={{ color: "#10B981" }}>{topScore}</span>
      </Typography>

      <Box className={classes.circleWrapper}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={progressSize}
          thickness={4}
          style={{ color: "#e0e0e0" }}
        />
        <CustomCircularProgress
          variant="determinate"
          value={percentage}
          size={progressSize}
          thickness={5}
          style={{ color, position: "absolute" }}
        />
        <Box className={classes.circleContent}>
          <Typography className={classes.progressValue} style={{ color }}>
            {score}
          </Typography>
          <Typography className={classes.progressSubtext}>
            out of {maxScore}
          </Typography>
        </Box>
      </Box>

      {/* Progress bars */}
      {[
        { label: "You", value: percentage, color },
        { label: "Avg", value: avgPercentage, color: "#A83B96" },
        { label: "Top", value: topPercentage, color: "#46AB87" },
      ].map((bar) => (
        <Box className={classes.progressRow} key={bar.label}>
          <Typography variant="caption" style={{ fontWeight: "bold" }}>
            {bar.label}
          </Typography>
          <Box className={classes.progressBar}>
            <Box
              className={classes.progressFill}
              style={{ backgroundColor: bar.color, width: `${bar.value}%` }}
            />
          </Box>
          <Typography
            variant="caption"
            style={{ minWidth: "30px", textAlign: "right", fontWeight: "bold" }}
          >
            {bar.value}%
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const BranchAnalysisCard = ({
  branchName = "",
  totalStudents = "0",
  subjectRanks = {},
  subjectScores = {},
  avgScores = {},
  topScores = {},
  branchRankEnabled = false,
  rankEnabled = false,
  rankData = {},
  overallRankdata = {},
  resultData = {},
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const allSubjects = new Set([
    ...Object.keys(subjectRanks),
    ...Object.keys(subjectScores),
    ...Object.keys(avgScores),
    ...Object.keys(topScores),
  ]);

  const subjects = Array.from(allSubjects).filter((s) => s !== "Overall");

  const ranks = {};
  subjects.forEach((s) => {
    ranks[s] = subjectRanks[s] || 0;
  });
  ranks.Overall = subjectRanks.Overall || 0;
const perSubjectMax = resultData.testTotalMarks / resultData.subjects.length;

  const marks = {};
  subjects.forEach((s) => {
    marks[s] = {
      score: subjectScores[s] || 0,
      maxScore: perSubjectMax,
      avg: avgScores[s] || 0,
      top: topScores[s] || 0,
    };
  });

  const totalScore = subjects.reduce(
    (sum, s) => sum + (subjectScores[s] || 0),
    0
  );
  const totalAvg = subjects.reduce((sum, s) => sum + (avgScores[s] || 0), 0);
  const totalTop = subjects.reduce((sum, s) => sum + (topScores[s] || 0), 0);

  marks.Total = {
    score: totalScore,
    maxScore: resultData.testTotalMarks,
    avg: totalAvg,
    top: totalTop,
  };

  const branchData = { branchName, totalStudents, ranks, marks };

  const getSubjectIcon = (subject) => {
    const lower = subject.toLowerCase();
    if (lower.includes("physics")) return "⚡";
    if (lower.includes("chemistry")) return "🧪";
    if (lower.includes("math")) return "📐";
    if (lower.includes("biology")) return "🧬";
    if (lower.includes("english")) return "📚";
    if (lower.includes("computer")) return "💻";
    if (lower.includes("overall") || lower.includes("total")) return "🏆";
    return "🏆";
  };

  const getSubjectColor = (subject) => {
    const lower = subject.toLowerCase();
    if (lower.includes("physics")) return "#2196F3";
    if (lower.includes("chemistry")) return "#FF9800";
    if (lower.includes("math")) return "#4CAF50";
    if (lower.includes("biology")) return "#8BC34A";
    if (lower.includes("english")) return "#E91E63";
    if (lower.includes("computer")) return "#607D8B";
    if (lower.includes("overall") || lower.includes("total")) return "#9C27B0";
    const colors = [
      "#F44336",
      "#9C27B0",
      "#673AB7",
      "#3F51B5",
      "#2196F3",
      "#03A9F4",
      "#00BCD4",
      "#009688",
      "#4CAF50",
      "#8BC34A",
      "#CDDC39",
      "#FFEB3B",
      "#FFC107",
      "#FF9800",
      "#FF5722",
    ];
    return colors[subject.length % colors.length];
  };

  return (
    <Grid container className={classes.container}>
      {rankEnabled && (
        <Grid item xs={12} className={classes.innerContent}>
          <ContentWrap>
            <Box
              className={classes.sectionTitle}
              display="flex"
              justifyContent="center"
            >
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                style={{ color: "#FF9800", fontWeight: "bold" }}
              >
                🏆 RANK&nbsp;
              </Typography>
              <Typography variant={isMobile ? "subtitle1" : "h6"} style={{ fontWeight: "bold" }}>
                ({overallRankdata.total} STUDENTS)
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {Object.entries(overallRankdata.subRanks).map(
                ([subject, rank]) => (
                  <Grid item xs={6} sm={3} key={subject}>
                    <SubjectRankCard
                      icon={getSubjectIcon(subject)}
                      name={subject}
                      rank={rank}
                      color={getSubjectColor(subject)}
                    />
                  </Grid>
                )
              )}
            </Grid>
          </ContentWrap>
        </Grid>
      )}

      {branchRankEnabled && (
        <Grid item xs={12} className={classes.innerContent}>
          <ContentWrap>
            <Box
              className={classes.sectionTitle}
              display="flex"
              justifyContent="center"
            >
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                style={{ fontWeight: "bold", color: "#FF9800" }}
              >
                🏆 Rank&nbsp;
              </Typography>
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                style={{ fontWeight: "bold" }}
              >
                Branch ({rankData.totalStudentsBranchWise} STUDENTS)
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {Object.entries(branchData.ranks).map(([subject, rank]) => (
                <Grid item xs={6} sm={3} key={subject}>
                  <SubjectRankCard
                    icon={getSubjectIcon(subject)}
                    name={subject}
                    rank={rank}
                    color={getSubjectColor(subject)}
                  />
                </Grid>
              ))}
            </Grid>
          </ContentWrap>
        </Grid>
      )}

      <Grid item xs={12} className={classes.innerContent}>
        <ContentWrap>
          <Box
            className={classes.sectionTitle}
            display="flex"
            justifyContent="center"
          >
            <Typography variant={isMobile ? "subtitle1" : "h6"} style={{ fontWeight: "bold" }}>
              📊 Marks&nbsp;
            </Typography>
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              style={{ fontWeight: "bold", color: "#FF9800" }}
            >
              ANALYSIS
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {Object.entries(branchData.marks).map(([subject, data]) => (
              <Grid item xs={6} sm={3} key={subject}>
                <CircularProgressCard
                  label={subject}
                  score={data.score}
                  maxScore={data.maxScore}
                  percentage={Math.round((data.score / data.maxScore) * 100)}
                  color={getSubjectColor(subject)}
                  avgScore={data.avg}
                  topScore={data.top}
                />
              </Grid>
            ))}
          </Grid>
        </ContentWrap>
      </Grid>
    </Grid>
  );
};

export default BranchAnalysisCard;
